import type { Funnel } from "@fbs/funnel-schema";
import { useEffect, useState } from "preact/hooks";

type Abschlussdaten = Funnel["completion"];
type Laderdaten = Abschlussdaten["loader"];

/**
 * Der Lader zwischen letztem Schritt und Danke-Seite.
 *
 * Er hat einen Zweck über die Optik hinaus: Der Nutzer soll sehen, dass etwas
 * mit seiner Anfrage passiert, statt auf einen eingefrorenen Knopf zu tippen.
 * Die wechselnden Zeilen erzählen dabei, was gerade geschieht, und keine
 * erfundene Geschichte.
 */
export function Lader({ loader }: { loader: Laderdaten }) {
  const [zeile, setZeile] = useState(0);
  const meldungen =
    loader.messages.length > 0 ? loader.messages : ["Anfrage wird gesendet"];

  useEffect(() => {
    if (meldungen.length < 2) return;

    const takt = Math.max(600, Math.round(loader.duration_ms / meldungen.length));
    const uhr = window.setInterval(() => {
      setZeile((alt) => Math.min(alt + 1, meldungen.length - 1));
    }, takt);

    return () => {
      window.clearInterval(uhr);
    };
  }, [loader.duration_ms, meldungen.length]);

  return (
    <div class="f-lader" role="status" aria-live="polite">
      <div class="f-lader__ring" aria-hidden="true" />
      <p class="f-lader__text">{meldungen[zeile] ?? meldungen[0]}</p>
    </div>
  );
}

export function Abschluss({
  abschluss,
  referenz,
}: {
  abschluss: Abschlussdaten;
  referenz: string | null;
}) {
  return (
    <div class="f-danke">
      <div class="f-danke__haken" aria-hidden="true">
        <svg viewBox="0 0 32 32" width="30" height="30">
          <path
            d="M8 16.5 13.5 22 24 10"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <h1 class="f-danke__titel">{abschluss.headline}</h1>
      {abschluss.body ? <p class="f-danke__text">{abschluss.body}</p> : null}

      {abschluss.next_steps.length > 0 ? (
        <ol class="f-danke__schritte">
          {abschluss.next_steps.map((eintrag, i) => (
            <li key={String(i)}>
              <span class="f-danke__nummer" aria-hidden="true">
                {eintrag.icon ?? String(i + 1)}
              </span>
              <span>{eintrag.text}</span>
            </li>
          ))}
        </ol>
      ) : null}

      {/*
        Ohne Anrede, aus demselben Grund wie bei den Prüfmeldungen: Ob ein
        Funnel duzt oder siezt, steht im Dokument. Was die Engine selbst
        beisteuert, darf sich nicht dagegen stellen.
      */}
      {abschluss.show_reference && referenz ? (
        <p class="f-danke__referenz">
          Anfragenummer: <strong>{referenz}</strong>
        </p>
      ) : null}
    </div>
  );
}
